// A BYO-producer that mirrors a site's blocks onto IPFS (szzt-spec.md §7.3, §13.1) — the
// IPFS half of items 6/7. Unlike the RASL/CDN producer, IPFS retrieval is keyed by the block's own
// CID via the standard gateway conventions (`https://<cid>.ipfs.<host>/` or `https://<host>/ipfs/<cid>`,
// see gateway/src/mirror.ts), so there is no `.well-known/rasl/` key mapping here; on a fully-successful
// push it advertises the gateway host as an `ipfs:<host>` hint so any gateway can race it.
//
// THE CODEC TRAP (why this is item 7, not free like the CDN). Our CIDs are single-block CIDv1 `raw`
// (0x55) for blobs / `dag-cbor` (0x71) for the manifest (§3.2). A naive `ipfs add` / pinning-service
// upload wraps a file in UnixFS `dag-pb` and re-chunks anything over 256 KiB under a DIFFERENT root
// CID — the `ipfs:<host>` hint would then 404. CID-exactness therefore requires storing each block as
// ONE raw/dag-cbor block under exactly our CID (Kubo `block/put --cid-codec …`, see kubo-block-put.ts),
// verifying the store reports that exact CID. That storage mechanism is the injected `BlockPut`; this
// module owns only the retrieval convention and the hint, exactly as `raslBucketProducer` owns the RASL
// key convention while delegating transport to an injected `BucketPut`.
import type { MirrorBlock, MirrorProducer } from "./types.ts";

/**
 * Store one content-addressed block so it later resolves under EXACTLY `block.cid` on an IPFS network,
 * resolving on success and REJECTING on any failure or CID mismatch (a rejection aborts the whole
 * publish's push → all-or-nothing → no hint written). HOW the block is stored (a Kubo RPC `block/put`,
 * a CAR import into a pinning service, an IPFS Cluster pin…) is the deployment's choice; this seam only
 * sees the block. A conforming impl MUST reject if the store reports a CID other than `block.cid` — a
 * hint pointing at a mirror that lacks the exact block is dead weight (§13.1).
 */
export type BlockPut = (block: MirrorBlock) => Promise<void>;

/**
 * Prefix marking a `hints` entry (§4.1) as an IPFS gateway host rather than a bare RASL host (§13.1).
 * MUST stay in lockstep with the gateway's `IPFS_HINT_PREFIX` (`gateway/src/mirror.ts`), which parses
 * it on the retrieval side — the two ends never share an import (publish must not depend on gateway).
 */
export const IPFS_HINT_PREFIX = "ipfs:";

/** The `hints` entry advertising an IPFS gateway host (§13.1): the bare host, `ipfs:`-prefixed. */
export function ipfsHint(gatewayHost: string): string {
  return `${IPFS_HINT_PREFIX}${gatewayHost}`;
}

/**
 * Build a {@link MirrorProducer} that mirrors blocks onto IPFS and advertises the public gateway
 * `gatewayHost` (a bare host like `ipfs.example.com`, served over https, that resolves
 * `https://<cid>.ipfs.<host>/` and/or `https://<host>/ipfs/<cid>`). Every block — resource blobs AND
 * the manifest — is stored via `put`; on full success `hints()` returns `[ipfs:<gatewayHost>]` so the
 * site record advertises the mirror. A `put` rejection propagates (publish()'s push is all-or-nothing).
 *
 * NOTE: `gatewayHost` (where blocks are SERVED and pinned) is typically NOT the same as the Kubo RPC
 * API the concrete `put` writes to — pin to your node's RPC, serve from its public gateway subdomain.
 */
export function ipfsBlockProducer(opts: { gatewayHost: string; put: BlockPut }): MirrorProducer {
  const { gatewayHost, put } = opts;
  return {
    async pushBlock(block: MirrorBlock): Promise<void> {
      await put(block);
    },
    hints(): string[] {
      return [ipfsHint(gatewayHost)];
    },
  };
}
